import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import {
  formatDateToShortString,
  BaseButton,
  Toast,
  useAuth,
  useStaleCheck,
  useLoginRequiredModal,
  useT,
} from '@repo/common';
import OptionList from '../../QuestionCard/OptionList';
import UserProfileModal from '../../Modal/UserProfileModal';
import AskQuestionModal from '../Modal/AskQuestionModal';
import AnswerModal from '../Modal/AnswerModal';

import { ReactComponent as NavigationIcon } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as HumanIcon } from '@repo/common/assets/icons/48px/icon_48px_human.svg';
import { ReactComponent as LikeIcon } from '@repo/common/assets/icons/20px/icon_20px_like_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

import {
  getQuestionDetail,
  getSingleQnaLike,
  addQnaLike,
  removeQnaLike,
} from '../../../api/qna/qna.api';

const Avatar = ({ profileUrl, alt, className }) => {
  if (
    !profileUrl ||
    profileUrl.includes('null') ||
    profileUrl.includes('undefined')
  ) {
    return (
      <HumanIcon className={twMerge('text-grayscale-gray06', className)} />
    );
  }

  let finalSrc = profileUrl;

  if (profileUrl.startsWith('data:image/') && profileUrl.includes('http')) {
    finalSrc = profileUrl.substring(profileUrl.indexOf('http'));
  } else if (
    !profileUrl.startsWith('http') &&
    !profileUrl.startsWith('data:image/')
  ) {
    finalSrc = `data:image/jpeg;base64,${profileUrl}`;
  }

  return (
    <img
      src={finalSrc}
      alt={alt || 'profile'}
      className={twMerge('rounded-full object-cover', className)}
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  );
};

const QnaDetailView = () => {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const { qnaId } = useParams();
  const { user, isLoggedIn } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();
  const [qnaDetail, setQnaDetail] = useState(null);
  const [answerData, setAnswerData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isHelpfulClicked, setIsHelpfulClicked] = useState(false);
  const [successToastVisible, setSuccessToastVisible] = useState(false);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);
  const [isOptionOpen, setIsOptionOpen] = useState(false);
  const desktopOptionRef = useRef(null);
  const mobileOptionRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [isOnlyTutor, setIsOnlyTutor] = useState(false);
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState({
    open: false,
    data: null,
  });

  useStaleCheck(qnaId ? `/qna/${qnaId}` : null);

  const openAskModal = () => {
    if (!isLoggedIn) {
      showLoginModal('qna_detail_ask_cta');
      return;
    }
    setIsAskModalOpen(true);
  };
  const closeAskModal = () => {
    setIsAskModalOpen(false);
    setIsOptionOpen(false);
  };

  const isAnswered =
    qnaDetail && qnaDetail.qnaAnswer && qnaDetail.qnaAnswer.content;
  const userRole = user?.role ?? null;
  const isAdmin = userRole === 'A';
  const buttonIcon = isAdmin ? isAnswered ? '' : <PlusIcon /> : <LikeIcon />;

  const buttonLabel = isAdmin
    ? isAnswered
      ? t('Edit answer', '답변 수정')
      : t('Add answer', '답변 추가')
    : t('Super helpful', '정말 도움돼요');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location.state?.showSuccessToast) {
      setSuccessToastVisible(true);

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchQnaDetail = async () => {
      try {
        const idToFetch = location.state?.newQnaId || qnaId;
        if (!idToFetch) return;

        const detail = await getQuestionDetail(idToFetch);
        setQnaDetail(detail);
      } catch (error) {
        console.error('Failed to fetch QnA detail', error);
      }
    };

    fetchQnaDetail();
  }, [location.state, qnaId]);

  useEffect(() => {
    if (qnaDetail) {
      setIsOnlyTutor(qnaDetail.tutorOnly);
      setAnswerData(qnaDetail.qnaAnswer);
    }
  }, [qnaDetail]);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!qnaId || !user) return;

      try {
        const likeData = await getSingleQnaLike(qnaId);

        setIsHelpfulClicked(!!likeData.id);
      } catch (error) {
        console.error('Failed to fetch like status:', error);
      }
    };

    fetchLikeStatus();
  }, [qnaId, user]);

  console.log(qnaDetail);

  const isMyCard = qnaDetail && qnaDetail.author.id === user?.id;

  const handleBackClick = (e) => {
    navigate('/community/qna');
  };

  const handleOptionIconClick = (e, ref) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isOptionOpen && ref?.current) {
      setAnchorRect(ref.current.getBoundingClientRect());
      setIsOptionOpen(true);
    }
  };

  const handleHelpfulClick = async (e) => {
    e.stopPropagation();

    if (!isLoggedIn) {
      showLoginModal('qna_detail_helpful_cta');
      return;
    }

    if (!qnaDetail) return;

    try {
      if (!isHelpfulClicked) {
        await addQnaLike(qnaDetail);
      } else {
        await removeQnaLike(qnaId);
      }

      setIsHelpfulClicked((prev) => !prev);
    } catch (error) {
      console.error('좋아요 등록/삭제 실패:', error);
    }
  };

  const handleAddAnswerClick = async (e) => {
    e.stopPropagation();

    if (isAnswered && isAdmin) {
      setEditData({
        answerId: answerData.id,
        content: answerData.content,
        author: answerData.author,
      });
    } else {
      setEditData(null);
    }

    setIsAnswerModalOpen(true);
  };

  const closeAnswerModal = () => {
    setIsAnswerModalOpen(false);
  };

  const handleAnswerUpdated = (updatedAnswer) => {
    setAnswerData(updatedAnswer);
    setQnaDetail((prev) => ({
      ...prev,
      qnaAnswer: updatedAnswer,
    }));
  };

  return (
    <div className='relative z-[10] h-auto w-full bg-grayscale-gray01 flex flex-col justify-center soft-60 tabletH:soft-70'>
      <div className='tabletH:hidden inset-0 z-[9999] relative flex items-center px-4 py-[20px] bg-grayscale-gray01'>
        <BaseButton
          type='text'
          size='l'
          icon={<NavigationIcon />}
          bgclass='bg-grayscale-gray01 border-none'
          textclass='text-icon-primary'
          onClick={handleBackClick}
        />
        <div className='absolute left-1/2 -translate-x-1/2 text-center'>
          <div className='typo-title2 text-grayscale-gray09 mb-2'>
            {t('Question card', '질문 카드')}
          </div>
          <div className='text-[10px] text-green-deep02'>
            {t('Community', '커뮤니티')} {'>'} Q&A {'>'}{' '}
            {qnaDetail?.category.name}
          </div>
        </div>
      </div>

      {/* 데스크탑 */}
      <div className='px-4 pt-[26px] pb-4 tabletH:p-0'>
        <div className='w-full bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 rounded-[40px] p-[30px] pb-10 tabletH:p-8 overflow-visible'>
          {/* 헤더 섹션 */}
          <div className='flex justify-between items-center tabletH:mb-10 max-h-[36px]'>
            <div className='hidden tabletH:flex items-center gap-2'>
              {/* 뒤로가기 버튼 */}
              <BaseButton
                type='text'
                size='l'
                icon={<NavigationIcon />}
                bgclass='bg-grayscale-gray00 border-none'
                textclass='text-icon-primary'
                onClick={handleBackClick}
              />

              <div className='typo-title2 text-grayscale-gray09'>
                {t('Question card', '질문 카드')}
              </div>
              <span className='text-grayscale-gray05'>·</span>
              <span
                className='typo-b14 text-green-deep02 cursor-pointer'
                onClick={() =>
                  navigate('/community/qna', {
                    state: {
                      categoryId: qnaDetail.category.id,
                    },
                  })
                }
              >
                {qnaDetail ? qnaDetail.category.name : ''}
              </span>
            </div>

            {/* 우측 상단 더보기 메뉴 */}
            <div className='hidden tabletH:inline-block'>
              <BaseButton
                ref={desktopOptionRef}
                type='text'
                size='l'
                icon={<OptionIcon />}
                bgclass='bg-grayscale-gray00 border-none'
                textclass={twMerge(
                  'text-icon-placeholder',
                  isOptionOpen && 'text-icon-primary',
                )}
                hoverclass='hover:bg-grayscale-gray00 hover:text-icon-primary'
                activeclass='active:bg-grayscale-gray00 active:text-icon-primary'
                onMouseDown={(e) => handleOptionIconClick(e, desktopOptionRef)}
              />

              {isOptionOpen && anchorRect && (
                <OptionList
                  anchorRect={anchorRect}
                  isMyCard={isMyCard}
                  data={qnaDetail}
                  isOnlyTutor={isOnlyTutor}
                  onClose={() => setIsOptionOpen(false)}
                  onDeleteSuccess={() => {
                    navigate('/community/qna');
                  }}
                  onUpdate={(key, value) => {
                    if (key === 'hidden') {
                      setQnaDetail((prev) => ({ ...prev, hidden: value }));
                    } else if (key === 'onlyTutor') {
                      setIsOnlyTutor(value);
                      setQnaDetail((prev) => ({ ...prev, tutorOnly: value }));
                    } else {
                      setQnaDetail((prev) => ({ ...prev, qnaAnswer: value }));
                    }
                  }}
                />
              )}
            </div>
          </div>

          {/* 사용자 정보 섹션 */}
          <div className='flex items-center justify-between tabletH:gap-4 mb-8'>
            <div className='flex items-center gap-2 tabletH:gap-4'>
              <div
                className='flex items-center justify-center cursor-pointer'
                onClick={() =>
                  qnaDetail &&
                  setShowProfileModal({ open: true, data: qnaDetail.author })
                }
              >
                {qnaDetail ? (
                  <Avatar
                    profileUrl={qnaDetail.author.profileUrl}
                    className='w-[39px] h-[39px] tabletH:w-[52px] tabletH:h-[52px]'
                  />
                ) : (
                  <HumanIcon className='text-grayscale-gray06' />
                )}
              </div>

              <div>
                <div
                  className='typo-b14Strong tabletH:typo-b20Strong text-grayscale-gray09 hover:underline cursor-pointer'
                  onClick={() =>
                    setShowProfileModal({ open: true, data: qnaDetail.author })
                  }
                >
                  {qnaDetail ? qnaDetail.author.firstName : ''}
                </div>
                <div className='typo-b12 tabletH:typo-b14 text-grayscale-gray05'>
                  {/* 모바일 */}
                  <div className='tabletH:hidden'>
                    <div>
                      {qnaDetail?.tutorOnly
                        ? t('Visible to tutor only', '튜터에게만 공개')
                        : t('Visible to everyone', '전체 공개')}
                    </div>
                    <div>{formatDateToShortString(qnaDetail?.modifiedAt)}</div>
                  </div>

                  {/* 태블릿 이상 */}
                  <div className='hidden tabletH:block'>
                    {qnaDetail &&
                      `${
                        qnaDetail.tutorOnly
                          ? t('Visible to tutor only', '튜터에게만 공개')
                          : t('Visible to everyone', '전체 공개')
                      } · ${formatDateToShortString(qnaDetail?.modifiedAt)}`}
                  </div>
                </div>
              </div>
            </div>

            {/* 모바일 옵션 버튼 */}
            <div className='relative inline-block tabletH:hidden pointer-events-auto'>
              <BaseButton
                ref={mobileOptionRef}
                type='text'
                size='l'
                icon={<OptionIcon />}
                bgclass='bg-grayscale-gray00 border-none h-[32px]'
                textclass={twMerge(
                  'text-icon-placeholder',
                  isOptionOpen && 'text-icon-primary',
                )}
                hoverclass='hover:bg-grayscale-gray00 hover:text-icon-primary'
                activeclass='active:bg-grayscale-gray00 active:text-icon-primary'
                onMouseDown={(e) => handleOptionIconClick(e, mobileOptionRef)}
                className='w-[32px] h-[32px]'
              />

              {isOptionOpen && anchorRect && (
                <OptionList
                  anchorRect={anchorRect}
                  isMyCard={isMyCard}
                  data={qnaDetail}
                  isOnlyTutor={isOnlyTutor}
                  onClose={() => setIsOptionOpen(false)}
                  onDeleteSuccess={() => {
                    navigate('/community/qna');
                  }}
                  onUpdate={(key, value) => {
                    if (key === 'hidden') {
                      setQnaDetail((prev) => ({ ...prev, hidden: value }));
                    } else if (key === 'onlyTutor') {
                      setIsOnlyTutor(value);
                      setQnaDetail((prev) => ({ ...prev, tutorOnly: value }));
                    } else {
                      setQnaDetail((prev) => ({ ...prev, qnaAnswer: value }));
                    }
                  }}
                />
              )}
            </div>
          </div>

          {/* 질문 본문 섹션 */}
          <div>
            <div className='flex items-start gap-2 mb-[20px]'>
              <span className='typo-h5 text-green-deep03'>Q.</span>
              <div className='typo-h5 text-grayscale-gray09 break-all w-full'>
                {qnaDetail ? qnaDetail.title : ''}
              </div>
            </div>

            {/*
              작성자가 textarea 에 넣은 줄바꿈·번호 목록을 그대로 보여준다.
              whitespace-pre-line 이 없으면 여러 줄이 한 문단으로 뭉쳐 버린다.
              break-words 를 쓰는 이유 — break-all 은 긴 URL 뿐 아니라 평범한
              단어도 아무 데서나 잘라 읽기 어렵게 만든다.
            */}
            <div className='text-text-primary pb-10 w-full whitespace-pre-line break-words'>
              {qnaDetail ? qnaDetail.content : ''}
            </div>
          </div>

          {/* Super helpful 버튼 */}
          <div className='hidden tabletH:flex justify-center pb-[60px]'>
            <BaseButton
              size='l'
              type='text_basic'
              icon={buttonIcon}
              iconPosition='after'
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
              }}
              onClick={isAdmin ? handleAddAnswerClick : handleHelpfulClick}
              width='100%'
              textWrapperClass='flex-none'
              bgclass={isHelpfulClicked ? 'bg-green-light01 border-none' : ''}
              textclass={isHelpfulClicked ? 'text-green-deep03' : ''}
              hoverclass={isHelpfulClicked ? 'hover:bg-green-light01' : ''}
            >
              {buttonLabel}
            </BaseButton>

            {isAnswerModalOpen && (
              <AnswerModal
                isOpen={isAnswerModalOpen}
                onClose={closeAnswerModal}
                qnaId={qnaDetail.id}
                initialData={editData}
                onAnswerUpdated={handleAnswerUpdated}
              />
            )}
          </div>

          {/* 답변 섹션 */}
          <div className='hidden tabletH:flex flex-col w-full min-h-[140px] bg-grayscale-gray01 rounded-[30px] p-10'>
            {isAnswered ? (
              <div>
                {/* 답변 작성자 정보 */}
                <div className='flex items-center mb-4'>
                  <div
                    className='w-[52px] h-[52px] mr-3 flex-shrink-0 cursor-pointer'
                    onClick={() =>
                      setShowProfileModal({
                        open: true,
                        data: qnaDetail.qnaAnswer.author,
                      })
                    }
                  >
                    {qnaDetail?.qnaAnswer?.author?.profileUrl ? (
                      <Avatar
                        profileUrl={qnaDetail.qnaAnswer.author.profileUrl}
                        className='w-full h-full rounded-full object-cover'
                      />
                    ) : (
                      <HumanIcon className='text-grayscale-gray06' />
                    )}
                  </div>

                  <div className='flex flex-col'>
                    <div className='flex items-center gap-2'>
                      <span
                        className='typo-b20Strong text-grayscale-gray09 hover:underline cursor-pointer'
                        onClick={() =>
                          setShowProfileModal({
                            open: true,
                            data: qnaDetail.qnaAnswer.author,
                          })
                        }
                      >
                        {qnaDetail.qnaAnswer.author.firstName}
                      </span>

                      {qnaDetail.qnaAnswer.author.role === 'A' && (
                        <BaseButton
                          size='s'
                          type='text'
                          hideIcon
                          bgclass='bg-orange-orange01 border border-orange-orange01 rounded-lg px-2'
                          textclass='text-orange-orange03'
                          hoverclass='hover:bg-orange-orange01 hover:text-orange-orange03'
                          activeclass='active:bg-orange-orange01 active:text-orange-orange03'
                          width='w-fit'
                        >
                          Tutor
                        </BaseButton>
                      )}
                    </div>

                    <div className='text-grayscale-gray05 typo-b14'>
                      {formatDateToShortString(qnaDetail.qnaAnswer.createdAt)}
                    </div>
                  </div>
                </div>

                {/*
                  답변 내용.
                  dangerouslySetInnerHTML 을 쓰지 않는다 — 답변은 textarea 로 받은
                  평문이라 HTML 로 해석할 이유가 없고, 그대로 두면 작성자가 넣은
                  태그가 실행된다. 평문으로 렌더하면서 줄바꿈만 살린다.
                */}
                <div className='text-text-primary min-h-[60px] px-[4px] break-words whitespace-pre-line text-left'>
                  {qnaDetail.qnaAnswer.content}
                </div>
              </div>
            ) : (
              <div className='flex flex-col justify-center items-start text-text-placeholder'>
                <p>{t('Please hold on…', '잠시만 기다려 주세요…')}</p>
                <p>
                  {t(
                    'Tutor’s answer is coming 😊',
                    '튜터의 답변이 곧 도착해요 😊',
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='tabletH:hidden flex justify-center px-4 pb-[50px]'>
        <BaseButton
          size='l'
          type='primary'
          icon={<LikeIcon />}
          iconPosition='after'
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
          }}
          onClick={handleHelpfulClick}
          width='100%'
          bgclass={`rounded-[30px] ${
            isHelpfulClicked ? 'bg-green-deep05' : ''
          }`}
          hoverclass={isHelpfulClicked ? 'hover:bg-green-deep05' : ''}
        >
          {t('Super helpful', '정말 도움돼요')}
        </BaseButton>
      </div>

      {/* 모바일 답변 섹션 */}
      <div className='tabletH:hidden w-full px-4 pb-4 flex flex-col'>
        <div className='flex items-start'>
          {/* 프로필 아이콘 */}
          <div
            className={twMerge(
              'w-[39px] h-[39px] mr-[6px] rounded-full bg-grayscale-gray02 flex-shrink-0',
              isAnswered && 'cursor-pointer',
            )}
            onClick={() =>
              isAnswered &&
              setShowProfileModal({
                open: true,
                data: qnaDetail.qnaAnswer.author,
              })
            }
          >
            {isAnswered && qnaDetail?.qnaAnswer?.author?.profileUrl ? (
              <Avatar
                profileUrl={qnaDetail.qnaAnswer.author.profileUrl}
                className='w-full h-full object-cover'
              />
            ) : (
              <HumanIcon className='w-full h-full text-grayscale-gray06' />
            )}
          </div>

          {/* 답변 */}
          <div className='flex flex-col flex-1'>
            {isAnswered && (
              <div className='flex flex-col pb-2'>
                <div className='flex items-center gap-2 mb-[2px]'>
                  <span
                    className='typo-b14Strong text-grayscale-gray09 underline cursor-pointer'
                    onClick={() =>
                      setShowProfileModal({
                        open: true,
                        data: qnaDetail.qnaAnswer.author,
                      })
                    }
                  >
                    {qnaDetail.qnaAnswer.author.firstName}
                  </span>

                  {qnaDetail.qnaAnswer.author.role === 'A' && (
                    <BaseButton
                      size='s'
                      type='text'
                      hideIcon
                      bgclass='bg-orange-orange01 border border-orange-orange01 rounded-lg px-2'
                      textclass='text-orange-orange03'
                      hoverclass='hover:bg-orange-orange01 hover:text-orange-orange03'
                      activeclass='active:bg-orange-orange01 active:text-orange-orange03'
                      width='w-fit'
                    >
                      {t('Tutor', '튜터')}
                    </BaseButton>
                  )}
                </div>

                <div className='text-grayscale-gray05 typo-b12'>
                  {formatDateToShortString(qnaDetail.qnaAnswer.createdAt)}
                </div>
              </div>
            )}

            {/* 말풍선 */}
            <div className='w-full bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 rounded-[30px] px-[30px] py-5'>
              {isAnswered ? (
                <div className='text-text-primary leading-relaxed break-words whitespace-pre-line'>
                  {qnaDetail.qnaAnswer.content}
                </div>
              ) : (
                <div className='flex flex-col text-text-tertiary'>
                  <p>{t('Please hold on...', '잠시만 기다려 주세요...')}</p>
                  <p>
                    {t(
                      'tutor’s answer is coming 😊',
                      '튜터의 답변이 곧 도착해요 😊',
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='flex flex-col items-center tabletH:min-h-[980px] tabletH:mt-[24px] tabletH:py-10 py-20'>
        <div className='typo-b14 text-text-placeholder mb-3'>
          {t(
            'Do you have any other questions?',
            '다른 궁금한 점이 있으신가요?',
          )}
        </div>

        {/* New question 버튼 */}
        <BaseButton
          size='m'
          type='text'
          bgclass='bg-grayscale-gray00 border-grayscale-gray00'
          textclass='text-grayscale-gray07'
          hoverclass='hover:bg-grayscale-gray00 hover:border-grayscale-gray00'
          hideIcon
          onClick={() => {
            openAskModal();
          }}
        >
          {t('New question', '새 질문')}
        </BaseButton>
        <AskQuestionModal isOpen={isAskModalOpen} onClose={closeAskModal} />
      </div>

      <div className='tabletH:hidden pb-[100px]' />

      {successToastVisible && (
        <div>
          <div className='hidden tabletH:fixed bottom-[24px] left-1/2 -translate-x-1/2 z-[1000]'>
            <Toast
              visibleOn='desktop'
              hierarchy='mostImportant'
              type='success'
              state='white'
              title={t('Your question is posted!', '질문이 등록되었습니다!')}
              direction='up'
              customWidth={'auto'}
            />
          </div>

          <div className='tabletH:hidden fixed top-[146px] left-1/2 -translate-x-1/2 z-[9999999]'>
            <Toast
              visibleOn='mobile'
              hierarchy='small'
              type='success'
              state='solid'
              title={t('Your question is posted!', '질문이 등록되었습니다!')}
              direction='down'
              customWidth={'190px'}
            />
          </div>
        </div>
      )}

      {showProfileModal.open && (
        <UserProfileModal
          data={showProfileModal.data}
          onClose={() => setShowProfileModal({ open: false, data: null })}
        />
      )}
    </div>
  );
};

export default QnaDetailView;
